import { useEffect, useRef } from "react";
import BetTag from "./BetTag";
import { tilt3D } from "@/lib/motion";

const STATUS = {
  NS:   { label: null, color: "var(--bc-sub, #71717A)", live: false },
  "1H": { label: "LIVE", color: "#22C55E", live: true },
  HT:   { label: "INT", color: "#F59E0B", live: false },
  "2H": { label: "LIVE", color: "#22C55E", live: true },
  FT:   { label: "FT", color: "var(--bc-sub, #52525B)", live: false },
  AET:  { label: "AET", color: "var(--bc-sub, #52525B)", live: false },
  PEN:  { label: "RIG", color: "#3B82F6", live: false },
  ET:   { label: "SUPPL", color: "#3B82F6", live: false },
  P:    { label: "RIG", color: "#3B82F6", live: false },
  CANC: { label: "ANNUL", color: "#EF4444", live: false },
  PST:  { label: "RIMAN", color: "#F59E0B", live: false },
};

function TeamRow({ logo, name }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
        {logo ? (
          <img src={logo} alt={name} className="max-w-full max-h-full object-contain"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <span className="text-xs font-heading font-bold" style={{ color: "var(--bc-sub, #52525B)" }}>
            {name?.charAt(0) || "?"}
          </span>
        )}
      </div>
      <span className="font-heading font-bold text-sm uppercase tracking-tight truncate"
        data-team-name
        style={{ color: "var(--bc-ink, #D4D4D8)", transition: "color 0.18s ease" }}>
        {name}
      </span>
    </div>
  );
}

export default function MatchCard({ match, isLast, onClick, predictionBadge, predictionData, cornerBadge, valueBadge, profileScoreBadge, dnaBadge }) {
  const status = STATUS[match.status] || { label: match.status, color: "var(--bc-sub, #71717A)", live: false };
  const hasScore = match.goals.home !== null && match.goals.away !== null;
  const isFinished = ["FT", "AET", "PEN"].includes(match.status);
  const tk = predictionData?.target_kitano;
  // Subtle 3-D tilt that follows the cursor — adds depth without screaming
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    return tilt3D(cardRef.current, { max: 6, perspective: 900, scaleHover: 1.012 });
  }, []);

  const matchTime = match.date
    ? new Date(match.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  const accentColor = status.live
    ? "#22C55E"
    : isFinished && predictionBadge
      ? predictionBadge === "green" ? "var(--bc-accent)" : "#EF4444"
      : "var(--bc-accent)";

  const isRed = isFinished && predictionBadge === "red";
  const neonColor = isRed ? "#EF4444" : "var(--bc-accent)";
  // World Cup / FIFA tournaments: server flags ``is_rainbow=true``.
  // We render an animated conic-gradient frame on those cards.
  const isRainbow = match.is_rainbow === true;
  const hoverBg = isRed
    ? "rgba(239,68,68,0.04)"
    : "color-mix(in srgb, var(--bc-accent) 4%, transparent)";
  const hoverInset = isRed
    ? "rgba(239,68,68,0.18)"
    : "color-mix(in srgb, var(--bc-accent) 18%, transparent)";
  const hoverGlow = isRed
    ? "rgba(239,68,68,0.25)"
    : "color-mix(in srgb, var(--bc-accent) 25%, transparent)";

  return (
    <button
      ref={cardRef}
      data-testid={`match-card-${match.id}`}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left relative overflow-hidden group"
      style={{
        background: "transparent",
        borderBottom: isLast ? "none" : "1px solid var(--bc-panel-elev, rgba(255,255,255,0.04))",
        transition: "background 0.18s ease, box-shadow 0.18s ease",
        ...(isRainbow ? {
          // Outer rainbow ring drawn with a SECOND element via box-shadow trick:
          // we use a `padding` + a `::before` would be ideal but inline styles
          // can't reach pseudo-elements, so we leverage outline + animated
          // background-clip on the wrapper instead. See `data-rainbow-frame`.
          // Background applied here is the rainbow gradient layered behind
          // the transparent content area.
          position: "relative",
          isolation: "isolate",
        } : {}),
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.background = hoverBg;
        el.style.boxShadow = `inset 0 0 0 1px ${hoverInset}, 0 0 18px -6px ${hoverGlow}`;
        const bar = el.querySelector("[data-accent-bar]");
        if (bar) { bar.style.width = "3px"; bar.style.boxShadow = `0 0 8px 2px ${neonColor}`; }
        el.querySelectorAll("[data-team-name]").forEach(n => { n.style.color = "#FFFFFF"; });
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.background = "transparent";
        el.style.boxShadow = "none";
        const bar = el.querySelector("[data-accent-bar]");
        if (bar) { bar.style.width = "2px"; bar.style.boxShadow = "none"; }
        el.querySelectorAll("[data-team-name]").forEach(n => { n.style.color = "#D4D4D8"; });
      }}
    >
      {/* Left accent bar — neon glow on hover */}
      <span
        data-accent-bar
        className="absolute left-0 top-0 bottom-0"
        style={{
          width: "2px",
          background: accentColor,
          transition: "width 0.18s ease, box-shadow 0.18s ease",
        }}
      />

      {/* FIFA World tournaments: animated rainbow border (always) + badge
          shown ONLY for the actual World Cup competitions (main + Club),
          NOT for qualification rounds. */}
      {isRainbow && (
        <>
          <span
            aria-hidden
            data-rainbow-frame
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              padding: "2px",
              background:
                "conic-gradient(from var(--bc-rainbow-angle, 0deg), #FF1744, #FF9100, #FFEA00, #00E676, #00B0FF, #651FFF, #FF1744)",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              animation: "bc-rainbow-rotate 6s linear infinite",
              opacity: 0.92,
              zIndex: 1,
            }}
          />
          {match.is_world_cup_main === true && (
            <span
              data-rainbow-badge
              style={{
                position: "absolute",
                top: 4,
                right: 6,
                padding: "0.05rem 0.4rem",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.52rem",
                letterSpacing: "0.12em",
                fontWeight: 700,
                textTransform: "uppercase",
                color: "#0a0a0a",
                background:
                  "linear-gradient(90deg, #FF1744, #FF9100, #FFEA00, #00E676, #00B0FF, #651FFF)",
                borderRadius: 2,
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              ★ Mondiale
            </span>
          )}
        </>
      )}

      {/* Result dot (finished + badge) */}
      {isFinished && predictionBadge && (
        <div className="flex-shrink-0 pl-1" data-testid={`badge-${match.id}`}>
          <div className="w-1.5 h-1.5 rounded-full"
            style={{ background: predictionBadge === "green" ? "#22C55E" : "#EF4444" }} />
        </div>
      )}

      {/* Time / Status */}
      <div className="w-12 flex-shrink-0 text-center">
        {status.label ? (
          <span
            className="font-data text-[10px] font-bold px-1.5 py-0.5 inline-block"
            style={{
              color: status.color,
              background: status.live ? "rgba(34,197,94,0.08)" : "transparent",
              border: status.live ? "1px solid rgba(34,197,94,0.15)" : "none",
              letterSpacing: "0.05em",
            }}
          >
            {status.live && (
              <span className="inline-block w-1 h-1 rounded-full mr-1 animate-live align-middle"
                style={{ background: "#22C55E" }} />
            )}
            {status.label}
            {status.live && match.elapsed && <span className="ml-0.5">{match.elapsed}'</span>}
          </span>
        ) : (
          <span className="font-data text-sm tabular-nums" style={{ color: "var(--bc-sub, #71717A)" }}>
            {matchTime}
          </span>
        )}
      </div>

      {/* Teams */}
      <div className="flex-1 min-w-0 space-y-2">
        <TeamRow logo={match.home_team.logo} name={match.home_team.name} />
        <TeamRow logo={match.away_team.logo} name={match.away_team.name} />
      </div>

      {/* Target Kitano mini (or corner/value/dna badge) */}
      {dnaBadge ? (
        <div
          data-testid={`dna-badge-${match.id}`}
          className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0 max-w-[160px]"
        >
          <span
            className="font-data uppercase tracking-widest"
            style={{ color: "#22FFA3", fontSize: "0.55rem", textShadow: "0 0 6px rgba(34,255,163,0.5)" }}
          >
            🧬 MATCH DNA
          </span>
          <span
            className="font-data tabular-nums text-base font-bold"
            style={{
              color: dnaBadge.score >= 70 ? "#22FFA3" : dnaBadge.score >= 50 ? "#F6C643" : "#B487FF",
              textShadow: dnaBadge.score >= 70 ? "0 0 8px rgba(34,255,163,0.6)" : "none",
              lineHeight: 1.05,
            }}
          >
            {Math.round(dnaBadge.score)}
          </span>
          <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>
            {dnaBadge.n ? `${dnaBadge.n} gemelle` : "—"}
            {dnaBadge.tier === 2 ? " · paese" : dnaBadge.tier === 3 ? " · global" : ""}
          </span>
        </div>
      ) : profileScoreBadge ? (
        <MiniProfileGauge badge={profileScoreBadge} matchId={match.id} />
      ) : cornerBadge ? (
        <div
          data-testid={`corner-badge-${match.id}`}
          className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0 max-w-[160px]"
        >
          <span
            className="font-data uppercase tracking-widest"
            style={{ color: "#06B6D4", fontSize: "0.55rem" }}
          >
            CORNER ATTESI
          </span>
          <span
            className="font-data tabular-nums text-xs"
            style={{ color: "var(--bc-ink, #FAFAFA)" }}
          >
            {cornerBadge.home} <span style={{ color: "var(--bc-sub, #52525B)" }}>vs</span> {cornerBadge.away}
          </span>
          <span
            className="font-data text-xs tabular-nums font-bold"
            style={{
              color: cornerBadge.diff >= 4 ? "#06B6D4" : cornerBadge.diff >= 2 ? "#A1A1AA" : "#52525B",
              textShadow: cornerBadge.diff >= 4 ? "0 0 8px rgba(6,182,212,0.6)" : "none",
            }}
          >
            Δ {cornerBadge.diff}
          </span>
        </div>
      ) : valueBadge ? (
        <div
          data-testid={`value-badge-${match.id}`}
          className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0 max-w-[180px]"
        >
          <span
            className="font-data uppercase tracking-widest"
            style={{ color: "var(--bc-accent)", fontSize: "0.55rem" }}
          >
            VALUE BET
          </span>
          <span
            className="font-data truncate w-full text-right text-[11px]"
            style={{ color: "var(--bc-ink, #FAFAFA)" }}
            title={valueBadge.bet}
          >
            {valueBadge.bet}
          </span>
          <div className="flex items-center gap-2 font-data text-xs tabular-nums">
            <span style={{ color: "var(--bc-accent)", fontWeight: 700 }}>
              {valueBadge.prob.toFixed(1)}%
            </span>
            <span style={{ color: "var(--bc-sub, #52525B)" }}>·</span>
            <span
              style={{
                color: valueBadge.value_pct >= 15 ? "var(--bc-accent)" : valueBadge.value_pct >= 8 ? "#06B6D4" : "#A1A1AA",
                fontWeight: 700,
                textShadow: valueBadge.value_pct >= 15 ? "0 0 6px var(--bc-glow)" : "none",
              }}
            >
              +{valueBadge.value_pct.toFixed(1)}%
            </span>
          </div>
        </div>
      ) : tk && (
        <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0 max-w-[160px]"
          data-testid={`tk-summary-${match.id}`}>
          <span className="font-data uppercase tracking-widest"
            style={{ color: tk.type === "no_bet" ? "#EF4444" : "#F59E0B", fontSize: "0.55rem" }}>
            TARGET KITANO
          </span>
          {tk.type === "no_bet" ? (
            <span className="font-data text-xs font-bold uppercase"
              style={{ color: "#EF4444" }}>
              NO BET
            </span>
          ) : (
            <>
              <span className="font-data truncate w-full text-right"
                style={{ color: "var(--bc-sub, #71717A)", fontSize: "0.65rem" }}>
                {tk.bet}
              </span>
              <span className="font-data text-xs tabular-nums font-medium"
                style={{ color: tk.prob > 70 ? "#22C55E" : "#71717A" }}>
                {tk.prob}%
              </span>
            </>
          )}
        </div>
      )}

      {/* Score */}
      {hasScore && (
        <div className="flex-shrink-0 w-8 text-right">
          <div className="flex flex-col items-end gap-1.5">
            <span className="font-data text-sm font-bold leading-none tabular-nums" style={{ color: "var(--bc-ink, #FFFFFF)" }}>
              {match.goals.home}
            </span>
            <span className="font-data text-sm font-bold leading-none tabular-nums" style={{ color: "var(--bc-ink, #FFFFFF)" }}>
              {match.goals.away}
            </span>
          </div>
        </div>
      )}

      {/* Arrow */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        className="flex-shrink-0 transition-transform duration-150 group-hover:translate-x-0.5"
        style={{ color: "#3F3F46" }}>
        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// ─── Mini Tachimetro per le MatchCard (sorting per Profilo di Gioco) ──
// Versione ultra-compatta dell'arco semicircolare di PlayProfilePanel,
// pensata per stare nello stesso slot del Target Kitano (max ~160px).
function MiniProfileGauge({ badge, matchId }) {
  const s = Math.max(0, Math.min(10, Number(badge?.score) || 0));
  const color = badge?.color || "#A78BFA";
  const label = badge?.label || "Profilo";
  // Geometria gauge
  const W = 86, H = 44;
  const cx = W / 2, cy = H - 6, r = 34;
  const angle = Math.PI + (s / 10) * Math.PI;
  const tipX = cx + r * Math.cos(angle);
  const tipY = cy + r * Math.sin(angle);
  const arcStart = `${cx - r},${cy}`;
  const arcEnd = `${cx + r},${cy}`;
  const tier = s >= 8 ? "ECCELLENTE" : s >= 6 ? "BUONA" : s >= 4 ? "MEDIA" : s >= 2 ? "BASSA" : "SCARSA";
  const gradId = `mg-grad-${matchId}`;
  return (
    <div
      data-testid={`profile-score-badge-${matchId}`}
      className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0"
      style={{ maxWidth: 160, minWidth: 96 }}
    >
      <span className="font-data uppercase tracking-widest"
        style={{ color, fontSize: "0.55rem", textShadow: `0 0 6px ${color}80` }}>
        {label}
      </span>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF6FD8" />
            <stop offset="50%" stopColor="#F6C643" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        {/* Sfondo arco */}
        <path
          d={`M ${arcStart} A ${r} ${r} 0 0 1 ${arcEnd}`}
          fill="none" stroke="var(--bc-border, rgba(255,255,255,0.07))"
          strokeWidth={4} strokeLinecap="round"
        />
        {/* Arco attivo (large-arc-flag SEMPRE 0) */}
        <path
          d={`M ${arcStart} A ${r} ${r} 0 0 1 ${tipX} ${tipY}`}
          fill="none" stroke={`url(#${gradId})`}
          strokeWidth={4} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 4px ${color}80)` }}
        />
        {/* Lancetta */}
        <line x1={cx} y1={cy} x2={tipX} y2={tipY}
          stroke={color} strokeWidth={1.6} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={2.5} fill="#020202" stroke={color} strokeWidth={1.2} />
        {/* Score number */}
        <text x={cx} y={cy - 8} textAnchor="middle"
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: 13, fontWeight: 800, fill: color,
            filter: `drop-shadow(0 0 4px ${color})`,
          }}>
          {s.toFixed(1)}
        </text>
      </svg>
      <span className="font-data uppercase tracking-wider"
        style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.5rem", letterSpacing: "0.14em" }}>
        {tier}
      </span>
    </div>
  );
}
